<template>
  <div>
    <!-- Фильтры -->
    <div class="ac-filters">
      <CustomSelect
        :options="versionsOptions"
        label="Версия договора 1"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion1Change"
        :value="selectedVersion1Id"
        placeholder="Выберите первую версию"
        :clearable="false"
        style="width: 300px;"
      />
      <CustomSelect
        :options="versionsOptions"
        label="Версия договора 2"
        option-label-key="version_label"
        option-value-key="smeta_version_id"
        @change="onVersion2Change"
        :value="selectedVersion2Id"
        placeholder="Выберите вторую версию"
        :clearable="false"
        style="width: 300px;"
      />
      <CustomSelect
        :options="entranceOptions"
        label="Подъезд"
        option-label-key="label"
        option-value-key="project_entrance_id"
        :value="selectedEntranceId"
        @change="onEntranceChange"
        placeholder="Все подъезды"
        :clearable="false"
        style="width: 180px;"
      />
      <CustomSelect
        :options="floorOptions"
        label="Этаж"
        option-label-key="label"
        option-value-key="floor_map_id"
        :value="selectedFloorMapId"
        @change="onFloorChange"
        placeholder="Все этажи"
        :clearable="false"
        :disable="!selectedEntranceId"
        style="width: 160px;"
      />
      <q-btn
        :disable="!selectedVersion1Id || !selectedVersion2Id || isLoading"
        color="primary"
        label="Сравнить"
        @click="compare"
        :loading="isLoading"
        unelevated
        class="tw-h-[40px] tw-text-white tw-rounded-md tw-capitalize"
      />

      <!-- Итоги -->
      <div v-if="hasCompared && totals" class="ac-totals">
        <table class="ac-totals-table">
          <thead>
            <tr>
              <th></th>
              <th class="ac-totals-ver ac-totals-ver--1">{{ version1Info?.contractor_agreement_list_type_name || version1Info?.reason_name || 'Версия 1' }}</th>
              <th class="ac-totals-ver ac-totals-ver--2">{{ version2Info?.contractor_agreement_list_type_name || version2Info?.reason_name || 'Версия 2' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="ac-totals-label">Итого СМР</td>
              <td class="ac-totals-val v1-tot">{{ fmtMoney(totals.smr_v1) }}</td>
              <td class="ac-totals-val v2-tot">{{ fmtMoney(totals.smr_v2) }}</td>
            </tr>
            <tr>
              <td class="ac-totals-label">Итого ТМЦ</td>
              <td class="ac-totals-val v1-tot">{{ fmtMoney(totals.tmc_v1) }}</td>
              <td class="ac-totals-val v2-tot">{{ fmtMoney(totals.tmc_v2) }}</td>
            </tr>
            <tr class="ac-totals-row--sep">
              <td class="ac-totals-label">Итого без НДС</td>
              <td class="ac-totals-val v1-tot">{{ fmtMoney(totals.total_without_nds_v1) }}</td>
              <td class="ac-totals-val v2-tot">{{ fmtMoney(totals.total_without_nds_v2) }}</td>
            </tr>
            <tr>
              <td class="ac-totals-label">НДС</td>
              <td class="ac-totals-val v1-tot">{{ fmtMoney(totals.nds_v1) }}</td>
              <td class="ac-totals-val v2-tot">{{ fmtMoney(totals.nds_v2) }}</td>
            </tr>
            <tr class="ac-totals-row--grand">
              <td class="ac-totals-label">Всего по смете</td>
              <td class="ac-totals-val v1-tot">{{ fmtMoney(totals.grand_db_v1) }}</td>
              <td class="ac-totals-val v2-tot">{{ fmtMoney(totals.grand_db_v2) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Переключатель "Только различия" -->
    <div v-if="hasCompared && rawRows.length" class="ac-diff-toggle">
      <label class="diff-toggle-label">
        <q-toggle
          v-model="onlyDiff"
          dense
          color="negative"
          size="sm"
        />
        <span class="diff-toggle-text">Только различия</span>
      </label>
    </div>

    <!-- Пустое состояние -->
    <div v-if="!selectedVersion1Id || !selectedVersion2Id" class="ac-empty">
      <q-icon name="info" size="48px" color="grey-5" />
      <p>Выберите две версии договоров для сравнения</p>
    </div>

    <!-- Загрузка -->
    <div v-else-if="isLoading" class="tw-flex tw-justify-center tw-py-10">
      <q-spinner color="primary" size="3em" />
    </div>

    <!-- Нет данных -->
    <div v-else-if="hasCompared && !flatList.length" class="ac-empty">
      <q-icon name="check_circle" size="48px" color="green" />
      <p>Нет данных для сравнения</p>
    </div>

    <!-- Таблица -->
    <div v-else-if="hasCompared && flatList.length">
      <div class="ac-table-wrap">
        <table class="ac-table">
          <thead>
            <tr>
              <th rowspan="2" class="th-name">Наименование</th>
              <th colspan="4" class="th-ver th-ver--1">
                {{ version1Info?.contractor_agreement_list_type_name || version1Info?.reason_name || 'без причины' }}
              </th>
              <th colspan="4" class="th-ver th-ver--2">
                {{ version2Info?.contractor_agreement_list_type_name || version2Info?.reason_name || 'без причины' }}
              </th>
            </tr>
            <tr>
              <th class="th-sub th-sub--1">Объём</th>
              <th class="th-sub th-sub--1">
                <div>Цена, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
              <th class="th-sub th-sub--1">
                <div>Сумма, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
              <th class="th-sub th-sub--1">
                <div>Сумма общая, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
              <th class="th-sub th-sub--2">Объём</th>
              <th class="th-sub th-sub--2">
                <div>Цена, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
              <th class="th-sub th-sub--2">
                <div>Сумма, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
              <th class="th-sub th-sub--2">
                <div>Сумма общая, тг</div>
                <div class="th-nds-hint">с НДС / без НДС</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(item, idx) in flatList" :key="idx">

              <!-- Заголовок группы -->
              <tr v-if="item.type === 'group'" class="tr-group">
                <td colspan="9" class="td-group">{{ item.name || 'Без группы' }}</td>
              </tr>

              <!-- Строка подъезд/этаж -->
              <tr v-if="item.type === 'place'" class="tr-place">
                <td colspan="9" class="td-place">Подъезд {{ item.entrance }}, Этаж {{ item.level_num }}</td>
              </tr>

              <!-- Строка работы -->
              <tr v-else-if="item.type === 'work'" class="tr-work" :class="{ 'tr-work--diff': item.hasDiff }">
                <td class="td-name td-work-name">
                  <div class="work-label">
                    <span class="work-name" :class="{ 'changed': item.hasDiff }">
                      {{ item.work_set_name }}
                      <span v-if="item.work_set_unit_name" class="unit-text">({{ item.work_set_unit_name }})</span>
                    </span>
                  </div>
                </td>
                <td class="td-num v1-cell">{{ fmt(item.work_amount_v1) }}</td>
                <td class="td-num v1-cell">
                  <div class="split-data">
                    <div class="split-data-top">{{ fmtMoney(item.work_price_v1) }}</div>
                    <div class="split-data-bottom">{{ fmtMoney(item.work_price_without_nds_v1) }}</div>
                  </div>
                </td>
                <td class="td-num v1-cell td-sum">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.work_sum_v1) }}
                      <span v-if="item.nds_percent_work_set" class="nds-inline">{{ item.nds_percent_work_set }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.work_sum_without_nds_v1) }}</div>
                  </div>
                </td>
                <td class="td-num v1-cell td-sum td-grand">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.grand_total_sum_v1) }}
                      <span v-if="item.nds_percent_work_set" class="nds-inline">{{ item.nds_percent_work_set }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.grand_total_sum_without_nds_v1) }}</div>
                  </div>
                </td>
                <td class="td-num v2-cell">{{ fmt(item.work_amount_v2) }}</td>
                <td class="td-num v2-cell">
                  <div class="split-data">
                    <div class="split-data-top">{{ fmtMoney(item.work_price_v2) }}</div>
                    <div class="split-data-bottom">{{ fmtMoney(item.work_price_without_nds_v2) }}</div>
                  </div>
                </td>
                <td class="td-num v2-cell td-sum">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.work_sum_v2) }}
                      <span v-if="item.nds_percent_work_set" class="nds-inline">{{ item.nds_percent_work_set }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.work_sum_without_nds_v2) }}</div>
                  </div>
                </td>
                <td class="td-num v2-cell td-sum td-grand">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.grand_total_sum_v2) }}
                      <span v-if="item.nds_percent_work_set" class="nds-inline">{{ item.nds_percent_work_set }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.grand_total_sum_without_nds_v2) }}</div>
                  </div>
                </td>
              </tr>

              <!-- Строка материала -->
              <tr v-else-if="item.type === 'material'" class="tr-material" :class="{ 'tr-material--diff': item.hasDiff }">
                <td class="td-name td-mat-name">
                  <span class="mat-name" :class="{ 'changed': item.hasDiff }">
                    {{ item.material_name }}
                    <span v-if="item.material_unit_name" class="unit-text">({{ item.material_unit_name }})</span>
                  </span>
                </td>
                <td class="td-num v1-cell">{{ fmt(item.material_amount_v1) }}</td>
                <td class="td-num v1-cell">
                  <div class="split-data">
                    <div class="split-data-top">{{ fmtMoney(item.material_price_v1) }}</div>
                    <div class="split-data-bottom">{{ fmtMoney(item.material_price_without_nds_v1) }}</div>
                  </div>
                </td>
                <td class="td-num v1-cell td-sum">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.material_sum_v1) }}
                      <span v-if="item.nds_percent_material" class="nds-inline">{{ item.nds_percent_material }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.material_sum_without_nds_v1) }}</div>
                  </div>
                </td>
                <td class="td-num v1-cell td-grand"></td>
                <td class="td-num v2-cell">{{ fmt(item.material_amount_v2) }}</td>
                <td class="td-num v2-cell">
                  <div class="split-data">
                    <div class="split-data-top">{{ fmtMoney(item.material_price_v2) }}</div>
                    <div class="split-data-bottom">{{ fmtMoney(item.material_price_without_nds_v2) }}</div>
                  </div>
                </td>
                <td class="td-num v2-cell td-sum">
                  <div class="split-data">
                    <div class="split-data-top">
                      {{ fmtMoney(item.material_sum_v2) }}
                      <span v-if="item.nds_percent_material" class="nds-inline">{{ item.nds_percent_material }}%</span>
                    </div>
                    <div class="split-data-bottom">{{ fmtMoney(item.material_sum_without_nds_v2) }}</div>
                  </div>
                </td>
                <td class="td-num v2-cell td-grand"></td>
              </tr>

            </template>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { getSmetaAgreementVersions, getSmetaAgreementHistoryCompareRaw, getProjectEntrances, getProjectEntranceFloors } from './services';
import CustomSelect from '@/components/form/CustomSelect';

export default {
  name: 'ProjectSmetaAgreementCompare',
  components: { CustomSelect },
  props: {
    projectId: { type: Number, required: true },
  },
  data() {
    return {
      versions: [],
      selectedVersion1Id: null,
      selectedVersion2Id: null,
      isLoading: false,
      rawRows: [],
      hasCompared: false,
      entrances: [],
      selectedEntranceId: null,
      floors: [],
      selectedFloorMapId: null,
      onlyDiff: false,
    };
  },
  computed: {
    versionsOptions() {
      return this.versions.map((v) => ({
        ...v,
        version_label: [
          v.contractor_agreement_list_type_name || v.reason_name || 'без причины',
          `(${v.created_at})`,
        ].join(' — '),
      }));
    },
    version1Info() {
      return this.versionsOptions.find((v) => v.smeta_version_id === this.selectedVersion1Id);
    },
    version2Info() {
      return this.versionsOptions.find((v) => v.smeta_version_id === this.selectedVersion2Id);
    },
    entranceOptions() {
      return [
        { project_entrance_id: null, label: 'Все подъезды' },
        ...this.entrances,
      ];
    },
    floorOptions() {
      return [
        { floor_map_id: null, label: 'Все этажи' },
        ...this.floors,
      ];
    },
    filteredRawRows() {
      return this.rawRows.filter((row) => {
        if (this.selectedEntranceId !== null && row.project_entrance_id !== this.selectedEntranceId) return false;
        if (this.selectedFloorMapId !== null && row.floor_map_id !== this.selectedFloorMapId) return false;
        return true;
      });
    },
    totals() {
      if (!this.filteredRawRows.length) return null;
      const seenWork = new Set();
      let smr_v1 = 0, smr_v2 = 0;
      let smr_no_nds_v1 = 0, smr_no_nds_v2 = 0;
      let tmc_v1 = 0, tmc_v2 = 0;
      let tmc_no_nds_v1 = 0, tmc_no_nds_v2 = 0;
      let grand_db_v1 = 0, grand_db_v2 = 0;
      let grand_db_no_nds_v1 = 0, grand_db_no_nds_v2 = 0;

      this.filteredRawRows.forEach((row) => {
        const wk = `${row.project_entrance_id}_${row.floor_map_id}_${row.work_set_id}`;
        if (!seenWork.has(wk)) {
          seenWork.add(wk);
          smr_v1      += Number(row.work_sum_v1 || 0);
          smr_v2      += Number(row.work_sum_v2 || 0);
          smr_no_nds_v1 += Number(row.work_sum_without_nds_v1 || 0);
          smr_no_nds_v2 += Number(row.work_sum_without_nds_v2 || 0);
          grand_db_v1     += Number(row.grand_total_sum_v1 || 0);
          grand_db_v2     += Number(row.grand_total_sum_v2 || 0);
          grand_db_no_nds_v1 += Number(row.grand_total_sum_without_nds_v1 || 0);
          grand_db_no_nds_v2 += Number(row.grand_total_sum_without_nds_v2 || 0);
        }
        tmc_v1      += Number(row.material_sum_v1 || 0);
        tmc_v2      += Number(row.material_sum_v2 || 0);
        tmc_no_nds_v1 += Number(row.material_sum_without_nds_v1 || 0);
        tmc_no_nds_v2 += Number(row.material_sum_without_nds_v2 || 0);
      });

      const r = (v) => Math.round(v * 100) / 100;
      const total_without_nds_v1 = r(smr_no_nds_v1 + tmc_no_nds_v1);
      const total_without_nds_v2 = r(smr_no_nds_v2 + tmc_no_nds_v2);
      const grand_v1 = r(smr_v1 + tmc_v1);
      const grand_v2 = r(smr_v2 + tmc_v2);

      return {
        smr_v1: r(smr_v1),
        smr_v2: r(smr_v2),
        tmc_v1: r(tmc_v1),
        tmc_v2: r(tmc_v2),
        total_without_nds_v1,
        total_without_nds_v2,
        nds_v1: r(grand_v1 - total_without_nds_v1),
        nds_v2: r(grand_v2 - total_without_nds_v2),
        grand_v1,
        grand_v2,
        grand_db_v1: r(grand_db_v1),
        grand_db_v2: r(grand_db_v2),
        grand_db_no_nds_v1: r(grand_db_no_nds_v1),
        grand_db_no_nds_v2: r(grand_db_no_nds_v2),
      };
    },
    flatList() {
      if (!this.filteredRawRows.length) return [];

      const sorted = [...this.filteredRawRows].sort((a, b) => {
        const ea = Number(a.entrance || 0), eb = Number(b.entrance || 0);
        if (ea !== eb) return ea - eb;
        const la = Number(a.level_num ?? 0), lb = Number(b.level_num ?? 0);
        if (la !== lb) return la - lb;
        const ga = Number(a.group_order_num ?? 9999), gb = Number(b.group_order_num ?? 9999);
        if (ga !== gb) return ga - gb;
        const gna = String(a.work_set_check_group_name || ''), gnb = String(b.work_set_check_group_name || '');
        if (gna !== gnb) return gna.localeCompare(gnb, 'ru');
        const wna = String(a.work_set_name || ''), wnb = String(b.work_set_name || '');
        if (wna !== wnb) return wna.localeCompare(wnb, 'ru');
        return String(a.material_name || '').localeCompare(String(b.material_name || ''), 'ru');
      });

      const result = [];
      let lastGroupKey = null;
      let lastWorkKey = null;
      let lastPlaceKey = null;

      sorted.forEach((row) => {
        const groupKey = `${row.entrance || ''}_${row.level_num ?? ''}_${row.work_set_check_group_id ?? ''}`;
        const placeKey = `${row.entrance || ''}_${row.level_num ?? ''}`;
        const workKey = `${row.entrance || ''}_${row.level_num ?? ''}_${row.work_set_id || ''}`;

        const wa1 = Number(row.work_amount_v1 || 0);
        const ws1 = Number(row.work_sum_v1 || 0);
        const wa2 = Number(row.work_amount_v2 || 0);
        const ws2 = Number(row.work_sum_v2 || 0);
        const workHasDiff = wa1 !== wa2 || ws1 !== ws2;

        const ma1 = Number(row.material_amount_v1 || 0);
        const ms1 = Number(row.material_sum_v1 || 0);
        const ma2 = Number(row.material_amount_v2 || 0);
        const ms2 = Number(row.material_sum_v2 || 0);
        const matHasDiff = row.material_name && (ma1 !== ma2 || ms1 !== ms2);

        const rowHasDiff = workHasDiff || matHasDiff;
        if (this.onlyDiff && !rowHasDiff) return;

        if (groupKey !== lastGroupKey) {
          lastGroupKey = groupKey;
          lastPlaceKey = null;
          result.push({ type: 'group', name: row.work_set_check_group_name });
        }

        if (placeKey !== lastPlaceKey) {
          lastPlaceKey = placeKey;
          result.push({ type: 'place', entrance: row.entrance, level_num: row.level_num });
        }

        if (workKey !== lastWorkKey) {
          lastWorkKey = workKey;
          result.push({
            type: 'work',
            work_set_name: row.work_set_name,
            work_set_unit_name: row.work_set_unit_name,
            work_amount_v1: wa1,
            work_price_v1: Number(row.work_price_v1 || 0),
            work_price_without_nds_v1: Number(row.work_price_without_nds_v1 || 0),
            work_sum_v1: ws1,
            work_sum_without_nds_v1: Number(row.work_sum_without_nds_v1 || 0),
            work_amount_v2: wa2,
            work_price_v2: Number(row.work_price_v2 || 0),
            work_price_without_nds_v2: Number(row.work_price_without_nds_v2 || 0),
            work_sum_v2: ws2,
            work_sum_without_nds_v2: Number(row.work_sum_without_nds_v2 || 0),
            nds_percent_work_set: row.nds_percent_work_set != null ? Number(row.nds_percent_work_set) : null,
            grand_total_sum_v1: Number(row.grand_total_sum_v1 || 0),
            grand_total_sum_without_nds_v1: Number(row.grand_total_sum_without_nds_v1 || 0),
            grand_total_sum_v2: Number(row.grand_total_sum_v2 || 0),
            grand_total_sum_without_nds_v2: Number(row.grand_total_sum_without_nds_v2 || 0),
            hasDiff: workHasDiff,
          });
        }

        if (row.material_name) {
          result.push({
            type: 'material',
            material_name: row.material_name,
            material_unit_name: row.material_unit_name,
            material_amount_v1: ma1,
            material_price_v1: Number(row.material_price_v1 || 0),
            material_price_without_nds_v1: Number(row.material_price_without_nds_v1 || 0),
            material_sum_v1: ms1,
            material_sum_without_nds_v1: Number(row.material_sum_without_nds_v1 || 0),
            material_amount_v2: ma2,
            material_price_v2: Number(row.material_price_v2 || 0),
            material_price_without_nds_v2: Number(row.material_price_without_nds_v2 || 0),
            material_sum_v2: ms2,
            material_sum_without_nds_v2: Number(row.material_sum_without_nds_v2 || 0),
            nds_percent_material: row.nds_percent_material != null ? Number(row.nds_percent_material) : null,
            hasDiff: ma1 !== ma2 || ms1 !== ms2,
          });
        }
      });

      return result;
    },
  },
  methods: {
    fmt(val) {
      if (val == null || Number(val) === 0) return '—';
      return Number(val).toLocaleString('ru-RU', { maximumFractionDigits: 4 });
    },
    fmtMoney(val) {
      if (val == null || Number(val) === 0) return '—';
      return Number(val).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    onVersion1Change(value) {
      this.selectedVersion1Id = value;
      this.rawRows = [];
      this.hasCompared = false;
    },
    onVersion2Change(value) {
      this.selectedVersion2Id = value;
      this.rawRows = [];
      this.hasCompared = false;
    },
    async onEntranceChange(value) {
      this.selectedEntranceId = value;
      this.selectedFloorMapId = null;
      this.floors = [];
      if (value) {
        const entrance = this.entrances.find((e) => e.project_entrance_id === value);
        if (entrance) {
          const res = await getProjectEntranceFloors(this.projectId, entrance.entrance);
          this.floors = res || [];
        }
      }
    },
    onFloorChange(value) {
      this.selectedFloorMapId = value;
    },
    async compare() {
      if (!this.selectedVersion1Id || !this.selectedVersion2Id) return;
      this.isLoading = true;
      this.rawRows = [];
      this.rawRows = await getSmetaAgreementHistoryCompareRaw(
        this.projectId,
        this.selectedVersion1Id,
        this.selectedVersion2Id,
      );
      this.hasCompared = true;
      this.isLoading = false;
    },
    async loadVersions() {
      const res = await getSmetaAgreementVersions(this.projectId);
      this.versions = res || [];
      if (this.versions.length >= 2) {
        this.selectedVersion1Id = this.versions[1].smeta_version_id;
        this.selectedVersion2Id = this.versions[0].smeta_version_id;
      }
    },
  },
  async mounted() {
    await this.loadVersions();
    const res = await getProjectEntrances(this.projectId);
    this.entrances = res || [];
  },
};
</script>

<style scoped lang="scss">
.ac-filters {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.ac-diff-toggle {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.diff-toggle-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
}

.diff-toggle-text {
  font-size: 12px;
  color: #666;
}

.ac-totals {
  margin-left: auto;
  flex-shrink: 0;
}

.ac-totals-table {
  border-collapse: collapse;
  font-size: 12px;
  min-width: 360px;

  th, td {
    padding: 3px 10px;
    white-space: nowrap;
  }
}

.ac-totals-ver {
  font-size: 11px;
  font-weight: 600;
  text-align: right;
  padding-bottom: 5px;
  border-bottom: 2px solid #e0e0e0;
}
.ac-totals-ver--1 { color: #b34700; }
.ac-totals-ver--2 { color: #1a4f8a; }

.ac-totals-label {
  color: #555;
  font-size: 12px;
  padding-right: 16px;
}

.ac-totals-val {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.v1-tot { color: #b34700; }
.v2-tot { color: #1a4f8a; }

.ac-totals-row--sep td {
  border-top: 1px solid #e0e0e0;
  padding-top: 5px;
}

.ac-totals-row--grand td {
  font-weight: 700;
  font-size: 13px;
  border-top: 2px solid #e0e0e0;
  padding-top: 5px;
}

.ac-empty {
  padding: 40px;
  background: #f5f5f5;
  border-radius: 8px;
  text-align: center;
  color: #666;
  p { margin: 16px 0 0 0; }
}

.ac-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.ac-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 860px;
}

/* Шапка */
.th-name {
  background: #f5f5f5;
  font-weight: 700;
  text-align: left;
  padding: 8px 12px;
  border: 1px solid #ddd;
  min-width: 260px;
  vertical-align: middle;
}

.th-ver {
  font-weight: 700;
  text-align: center;
  padding: 8px 12px;
  border: 1px solid #ddd;
}
.th-ver--1 {
  background: #fff3e0;
  color: #e65100;
  border-left: 2px solid #ffcc80;
}
.th-ver--2 {
  background: #e3f2fd;
  color: #1565c0;
  border-left: 2px solid #90caf9;
}

.th-sub {
  font-weight: 500;
  font-size: 11px;
  text-align: right;
  padding: 5px 8px;
  border: 1px solid #ddd;
  white-space: nowrap;
}
.th-sub--1 {
  background: #fafafa;
  &:first-of-type { border-left: 2px solid #ffcc80; }
}
.th-sub--2 {
  background: #f0f8ff;
  &:first-of-type { border-left: 2px solid #90caf9; }
}

/* Заголовок группы */
.tr-group .td-group {
  padding: 6px 12px;
  font-weight: 700;
  font-size: 12px;
  color: #1a237e;
  background: #f0f4ff;
  border-top: 2px solid #c5cae9;
  border-bottom: 1px solid #c5cae9;
}

/* Строка подъезд/этаж */
.tr-place .td-place {
  padding: 4px 16px;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
}

/* Строка работы */
.tr-work {
  background: #fafafa;

  td {
    border: 1px solid #e0e0e0;
    padding: 6px 8px;
    vertical-align: middle;
  }

  &.tr-work--diff td {
    background: #ffebee;
  }
}

/* Строка материала */
.tr-material {
  background: #fff;

  td {
    border: 1px solid #eeeeee;
    border-top: none;
    padding: 4px 8px;
    vertical-align: middle;
  }

  &.tr-material--diff td {
    background: #ffebee;
  }
}

/* Общие ячейки */
.td-name { text-align: left; }

.td-work-name {
  border-left: 3px solid #c5cae9 !important;
}

.td-mat-name {
  padding-left: 24px !important;
  border-left: 3px solid #e0e0e0 !important;
}

.td-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.td-sum { font-weight: 600; }

.td-grand {
  background: rgba(0, 0, 0, 0.02);
  font-weight: 700;
}

.v1-cell {
  color: #b34700;
  &:first-of-type { border-left: 2px solid #ffcc80 !important; }
}

.v2-cell {
  color: #1a4f8a;
  &:first-of-type { border-left: 2px solid #90caf9 !important; }
}

.work-name {
  font-size: 12px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
}

.mat-name {
  font-size: 11px;
  color: #374151;
}

.unit-text {
  color: #9ca3af;
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
}

.changed {
  font-weight: 700;
}

.split-data {
  display: flex;
  flex-direction: column;

  .split-data-top {
    padding: 2px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
  }

  .split-data-bottom {
    padding: 2px 0;
    color: #555;
    font-size: 11px;
  }
}

.nds-inline {
  display: inline-block;
  font-size: 10px;
  color: #9e9e9e;
  font-style: italic;
  font-weight: normal;
  line-height: 1.1;
}

.th-nds-hint {
  font-size: 9px;
  font-weight: 400;
  color: #9e9e9e;
  margin-top: 2px;
}
</style>
